/**
 * Shared app-update contract for the IntElux shell and SPFT MFE.
 * Button is shown only while a newer SW build is waiting, or when the
 * running JS cannot load a lazy chunk (stale deploy). After click + hard
 * reload the flag is gone and the button stays hidden.
 */

export const APP_UPDATE_AVAILABLE_EVENT = 'insitestore:app-update-available';
export const APP_UPDATE_CLEARED_EVENT = 'insitestore:app-update-cleared';
export const APP_UPDATE_APPLY_EVENT = 'insitestore:app-update-apply';

const UPDATE_FLAG = '__INSITESTORE_APP_UPDATE_AVAILABLE__';
const APPLIED_HASH_KEY = 'insitestore-applied-sw-hash';
const PENDING_HASH_KEY = 'insitestore-pending-sw-hash';
const STALE_RELOAD_KEY = 'insitestore-stale-chunk-reloaded';

type AppUpdateWindow = Window & {
  [UPDATE_FLAG]?: boolean;
};

function getWindow(): AppUpdateWindow | null {
  return typeof window === 'undefined' ? null : (window as AppUpdateWindow);
}

export function isAppUpdateAvailable(): boolean {
  return getWindow()?.[UPDATE_FLAG] === true;
}

export function getPendingAppUpdateHash(): string | null {
  if (typeof sessionStorage === 'undefined') {
    return null;
  }
  return sessionStorage.getItem(PENDING_HASH_KEY);
}

export function wasAppUpdateAlreadyApplied(hash: string | null | undefined): boolean {
  if (!hash || typeof sessionStorage === 'undefined') {
    return false;
  }
  return sessionStorage.getItem(APPLIED_HASH_KEY) === hash;
}

export function rememberPendingAppUpdateHash(hash: string): void {
  if (typeof sessionStorage === 'undefined') {
    return;
  }
  sessionStorage.setItem(PENDING_HASH_KEY, hash);
}

export function notifyAppUpdateAvailable(hash?: string): void {
  const win = getWindow();
  if (!win) {
    return;
  }
  if (hash && wasAppUpdateAlreadyApplied(hash)) {
    clearAppUpdateAvailable();
    return;
  }
  if (hash) {
    rememberPendingAppUpdateHash(hash);
  }
  win[UPDATE_FLAG] = true;
  win.dispatchEvent(new CustomEvent(APP_UPDATE_AVAILABLE_EVENT));
}

export function clearAppUpdateAvailable(): void {
  const win = getWindow();
  if (!win) {
    return;
  }
  win[UPDATE_FLAG] = false;
  if (typeof sessionStorage !== 'undefined') {
    sessionStorage.removeItem(PENDING_HASH_KEY);
  }
  win.dispatchEvent(new CustomEvent(APP_UPDATE_CLEARED_EVENT));
}

export function markAppUpdateApplied(hash?: string | null): void {
  const applied = hash || getPendingAppUpdateHash();
  if (applied && typeof sessionStorage !== 'undefined') {
    sessionStorage.setItem(APPLIED_HASH_KEY, applied);
  }
  if (typeof sessionStorage !== 'undefined') {
    sessionStorage.removeItem(STALE_RELOAD_KEY);
  }
  clearAppUpdateAvailable();
}

export function hasAlreadyReloadedForStaleChunks(): boolean {
  return typeof sessionStorage !== 'undefined' && sessionStorage.getItem(STALE_RELOAD_KEY) === '1';
}

export function markReloadedForStaleChunks(): void {
  if (typeof sessionStorage === 'undefined') {
    return;
  }
  sessionStorage.setItem(STALE_RELOAD_KEY, '1');
}

export function isStaleChunkLoadMessage(value: unknown): boolean {
  const text = value instanceof Error ? `${value.name} ${value.message}` : String(value ?? '');
  return /Failed to fetch dynamically imported module|error loading dynamically imported module|Loading chunk|ChunkLoadError|Importing a module script failed/i.test(
    text,
  );
}

/** true/false as the waiting-update state changes. After reload this starts as false. */
export function subscribeAppUpdateState(
  onChange: (available: boolean) => void,
): () => void {
  const win = getWindow();
  if (!win) {
    return () => undefined;
  }
  const onYes = () => onChange(true);
  const onNo = () => onChange(false);
  win.addEventListener(APP_UPDATE_AVAILABLE_EVENT, onYes);
  win.addEventListener(APP_UPDATE_CLEARED_EVENT, onNo);
  onChange(isAppUpdateAvailable());
  return () => {
    win.removeEventListener(APP_UPDATE_AVAILABLE_EVENT, onYes);
    win.removeEventListener(APP_UPDATE_CLEARED_EVENT, onNo);
  };
}

/** Headers call this; the Angular shell activates the SW then hard-reloads. */
export function applyHostAppUpdate(): void {
  getWindow()?.dispatchEvent(new CustomEvent(APP_UPDATE_APPLY_EVENT));
}

export function subscribeAppUpdateApply(onApply: () => void): () => void {
  const win = getWindow();
  if (!win) {
    return () => undefined;
  }
  const handler = () => onApply();
  win.addEventListener(APP_UPDATE_APPLY_EVENT, handler);
  return () => win.removeEventListener(APP_UPDATE_APPLY_EVENT, handler);
}
